// Telling per antwoordoptie van een keuzevraag, voor één meetmoment.
//
// Nodig omdat MultipleData deze telling inline deed en opties met 0 antwoorden
// wegliet. Dan zag een lezer niet welke opties er wél waren maar niet gekozen
// zijn (updateverzoek Drenthe beweegt, 2026-10-02). Nu staan alle opties erin.

import formatMomentLabel from '../formatMomentLabel'

export const OTHER_DISPLAY_LABEL = 'Anders, namelijk..'

/**
 * @typedef {object} MultipleOption
 * @property {string} id - ID van de MultipleQuestionOptions-rij
 * @property {string} name - Tekst van de optie
 */

/**
 * @typedef {object} OptionCount
 * @property {string} id
 * @property {string} name
 * @property {number} count - Aantal antwoorden; 0 als niemand de optie koos
 * @property {number} optIndex - Positie, bepaalt de kleur
 * @property {boolean} [isOther] - De 'Anders, namelijk'-bak
 */

/**
 * @param {Array<{Input?: string, OptionType?: string}>} slice - Antwoorden van één meetmoment
 * @param {MultipleOption[]} fixedOptions - Vaste opties in volgorde, zonder 'anders'
 * @returns {OptionCount[]}
 */
const countMultipleOptions = (slice, fixedOptions) => {
    const counts = fixedOptions.map((opt, optIndex) => ({
        id: opt.id,
        name: opt.name,
        count: slice.filter(r => r.Input === opt.name && r.OptionType !== 'other').length,
        optIndex,
    }))

    const otherCount = slice.filter(r => r.OptionType === 'other').length
    if (otherCount === 0) return counts

    return [
        ...counts,
        { id: '__other__', name: OTHER_DISPLAY_LABEL, count: otherCount, isOther: true, optIndex: fixedOptions.length },
    ]
}

/**
 * Positie van het meetmoment van een antwoord, of null als die niet bekend is.
 * NaN telt als onbekend: met NaN past een antwoord bij geen enkel meetmoment.
 *
 * @param {object} record - Antwoord uit useHierarchyData
 * @param {Object<string, number>} id2pos - MeasureMoments.ID → Position
 * @returns {number|null}
 */
const momentPositionOf = (record, id2pos) => {
    const raw = record.MomentPosition ?? id2pos[record.MomentID] ?? null
    const pos = typeof raw === 'number' ? raw : parseInt(raw, 10)
    return Number.isFinite(pos) ? pos : null
}

/**
 * @typedef {object} MomentGroup
 * @property {string} moment - Label voor de as, 'Voormeting (1)' of 'Meetmoment'
 * @property {number|null} momentPosition - null: meetmoment onbekend
 * @property {OptionCount[]} responses - Telling per optie
 * @property {string[]} others - Alle 'anders'-teksten
 */

/**
 * Antwoorden op een keuzevraag per meetmoment geteld.
 *
 * Antwoorden zonder bekend meetmoment (een verwijderde vragenlijst, een vraag
 * zonder onderzoek) vallen in één bak 'Meetmoment', achteraan. Voorheen vielen
 * ze weg en stond er een grafiek met alleen nullen naast het aantal invullingen.
 *
 * @param {Array<object>} records - Antwoorden uit useHierarchyData
 * @param {MultipleOption[]} fixedOptions - Vaste opties in volgorde
 * @param {Object<string, number>} id2pos - MeasureMoments.ID → Position
 * @param {Object<number, string>} pos2title - Position → MeasureMoments.Title
 * @returns {MomentGroup[]}
 */
export const groupMultipleByMoment = (records, fixedOptions, id2pos, pos2title) => {
    const others = records.filter(r => r.OptionType === 'other' && r.Input).map(r => r.Input)
    const withPos = records.map(r => ({ record: r, pos: momentPositionOf(r, id2pos) }))
    const positions = [...new Set(withPos.map(x => x.pos))]
        .sort((a, b) => (a === null ? Infinity : a) - (b === null ? Infinity : b))

    return positions.map(pos => ({
        moment: formatMomentLabel(pos === null ? undefined : pos2title?.[pos], pos),
        momentPosition: pos,
        responses: countMultipleOptions(withPos.filter(x => x.pos === pos).map(x => x.record), fixedOptions),
        others,
    }))
}

export default countMultipleOptions
